interface NamedThirdParty {
  readonly name?: string | null;
  readonly lastName?: string | null;
  readonly firstName?: string | null;
}

interface OrderedActor {
  readonly categoryIds: readonly string[];
  readonly thirdParty?: NamedThirdParty | null;
}

interface OrderedRepresentative {
  readonly person?: NamedThirdParty | null;
}

// The name a third party is sorted by: an organization by its name, a person by its last name
// then first name (as the third party lists).
const sortingNameOf = (thirdParty: NamedThirdParty | null | undefined) =>
  [thirdParty?.name, thirdParty?.lastName, thirdParty?.firstName]
    .filter(Boolean)
    .join(' ');

const compareNames = (
  first: NamedThirdParty | null | undefined,
  second: NamedThirdParty | null | undefined,
) =>
  sortingNameOf(first).localeCompare(sortingNameOf(second), 'fr', {
    sensitivity: 'base',
  });

// The actors of a list alphabetically, the ones of the leading category first (the quotation
// emitters of the internal list, the quotation receivers of the external one).
export const orderedActorsOf = <A extends OrderedActor>(
  actors: readonly A[],
  leadingCategoryId: string,
): readonly A[] => {
  const isLeading = ({ categoryIds }: A) =>
    categoryIds.includes(leadingCategoryId);

  return [...actors].sort(
    (first, second) =>
      Number(isLeading(second)) - Number(isLeading(first)) ||
      compareNames(first.thirdParty, second.thirdParty),
  );
};

export const orderedRepresentativesOf = <R extends OrderedRepresentative>(
  representatives: readonly R[],
): readonly R[] =>
  [...representatives].sort((first, second) =>
    compareNames(first.person, second.person),
  );
